import ParticlesCanvas from '@/components/ParticlesCanvas';
import { Button } from '@/components/ui/button';
import { CountryCodeSelect } from '@/components/ui/country-code-select';
import { Input } from '@/components/ui/input';
import { useAuthRedux } from '@/hooks/use-auth-redux';
import { useToast } from '@/hooks/use-toast';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const Login = () => {
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [countryCode, setCountryCode] = useState('+1'); // Default to US
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [otpAttempts, setOtpAttempts] = useState(0);
  const { signInWithPhone, verifyOtp } = useAuthRedux();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Countdown timer for resend
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Start countdown when OTP is sent
  const startResendCountdown = useCallback(() => {
    setCountdown(30); // 30 seconds countdown
  }, []);

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await signInWithPhone(countryCode + phone);

      if (error) {
        toast({
          title: 'Error',
          description: error.message || 'Failed to send OTP. Please try again.',
          variant: 'destructive',
        });
      } else {
        setStep('otp');
        startResendCountdown();
        toast({
          title: 'OTP Sent',
          description: 'Please check your phone for the verification code.',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0 || resendLoading) return;

    setResendLoading(true);
    try {
      const { error } = await signInWithPhone(countryCode + phone);

      if (error) {
        toast({
          title: 'Error',
          description:
            error.message || 'Failed to resend OTP. Please try again.',
          variant: 'destructive',
        });
      } else {
        startResendCountdown();
        toast({
          title: 'OTP Resent',
          description: 'A new verification code has been sent to your phone.',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setResendLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error, user } = await verifyOtp(countryCode + phone, otp);

      if (error) {
        setOtpAttempts((prev) => prev + 1);

        // Show different messages based on attempts
        let errorMessage =
          error.message || 'Please enter the correct verification code.';

        if (otpAttempts >= 2) {
          errorMessage = 'Multiple failed attempts. Please request a new OTP.';
          setOtp('');
          setOtpAttempts(0);
        }

        toast({
          title: 'Invalid OTP',
          description: errorMessage,
          variant: 'destructive',
        });
      } else if (user) {
        console.log('✅ Login successful, user:', user);
        console.log('🔄 Navigating to dashboard...');

        toast({
          title: 'Success',
          description: 'Successfully logged in!',
        });

        // Add a small delay to ensure Redux state is updated
        setTimeout(() => {
          navigate('/');
        }, 100);
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen w-full relative flex items-center justify-center p-4 overflow-hidden">
      <style>
        {`
          @keyframes float {
            0% {
              transform: translateY(0) translateX(0);
              opacity: 0.6;
            }
            50% {
              transform: translateY(-20px) translateX(10px);
              opacity: 0.8;
            }
            100% {
              transform: translateY(0) translateX(0);
              opacity: 0.6;
            }
          }
          .animate-float {
            animation: float 6s ease-in-out infinite;
          }
        `}
      </style>

      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url("/images/ai-eye.jpg")',
            filter: 'brightness(0.7) contrast(1.2)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-purple-900/50 to-slate-900/80" />
      </div>

      {/* Animated Particles Canvas */}
      <ParticlesCanvas />

      {/* Login Card with Vision OS Style */}
      <div className="relative z-20 w-full max-w-sm">
        <div className="relative">
          {/* Glass Card */}
          <div className="absolute inset-0 bg-black/10 backdrop-blur-md rounded-3xl border border-white/30 shadow-2xl" />

          {/* Inner Content */}
          <div className="relative p-8">
            {/* Logo and Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-purple-500 to-blue-800 flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-2xl">EV</span>
              </div>
              <h1 className="text-2xl font-bold text-white mb-2 tracking-wide">
                EyeVantage
              </h1>
              <p className="text-white/90 text-md tracking-wide">
                Ai-Powered Practice Intelligence
              </p>
            </div>

            {step === 'phone' ? (
              <form onSubmit={handlePhoneSubmit} className="space-y-6">
                <div>
                  <label className="block text-md font-medium text-white mb-2">
                    Phone Number
                  </label>
                  <div className="flex">
                    <CountryCodeSelect
                      value={countryCode}
                      onValueChange={(value) => setCountryCode(value)}
                    />
                    <Input
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="flex-1 h-12 px-4 py-3 bg-black/30 backdrop-blur-sm border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent rounded-r-xl rounded-l-none border-l-0 text-sm"
                      required
                    />
                  </div>
                  <p className="text-md text-white/70 mt-2">
                    We'll send a verification code to this number
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-purple-500 to-blue-800 hover:from-purple-600 hover:to-blue-800 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleOtpSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Verification Code
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    className="w-full h-12 px-4 py-3 bg-black/30 backdrop-blur-sm border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent rounded-xl text-center text-lg tracking-widest text-sm"
                    maxLength={6}
                    required
                  />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-sm text-white/90">
                      Sent to {countryCode + phone}
                    </p>
                    <button
                      type="button"
                      onClick={() => setStep('phone')}
                      className="text-sm text-purple-300 hover:text-purple-200 underline">
                      Change
                    </button>
                  </div>
                </div>

                {/* Resend OTP Section */}
                <div className="text-center">
                  {countdown > 0 ? (
                    <p className="text-xs text-white/70">
                      Resend available in {formatCountdown(countdown)}
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={resendLoading}
                      className="text-sm text-purple-300 hover:text-purple-200 underline disabled:opacity-50 disabled:cursor-not-allowed">
                      {resendLoading ? 'Sending...' : 'Resend OTP'}
                    </button>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="w-full h-12 bg-gradient-to-r from-purple-500 to-blue-800 hover:from-purple-600 hover:to-blue-800 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? 'Verifying...' : 'Verify & Login'}
                </Button>
              </form>
            )}

            <div className="mt-8 text-center">
              <p className="text-xs text-white/90">Secured Login</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
